import { Store, StoreComponentStatus } from "../../../../types";
import { DeploymentBranchesResult, NetlifySiteResult, shopifyAppsUrl } from "../../../../lib/storeComponents";
import NetlifyDeletePreview from "../../../../assets/netlify-delete-project.png";
import RunPipelinePreview from "../../../../assets/bitbucket-run-pipeline.png";
import DeleteBranchesPreview from "../../../../assets/bitbucket-delete-branches.png";
import { ComponentStatuses } from "./useComponentStatuses";

export type StepKey =
    | "syncOff"
    | "offline"
    | "searchIndex"
    | "mirror"
    | "shopify"
    | "basicTheme"
    | "hydrogenV2"
    | "netlify"
    | "cancel";

export type StepKind = "automatic" | "manual" | "final";

export type StepState = "checking" | "locked" | "ready" | "done";

export type Step = {
    key: StepKey;
    kind: StepKind;
    title: string;
    detail: string;
    description: string;
    state: StepState;
    doneNote?: string;
    lockedReason?: string;
    warning?: string;
    action?: { label: string; tone: "primary" | "danger" };
    link?: StepLink;
};

export type StepLink = { label: string; href: string; previews?: LinkPreview[] };

// Image size in pixels, so the tooltip is laid out before the image loads
export type LinkPreview = { image: string; width: number; height: number; caption: string };

const BITBUCKET_WORKSPACE_URL = "https://bitbucket.org/alexwielerrs";

const DELETE_BRANCHES_PREVIEWS: LinkPreview[] = [
    { image: RunPipelinePreview, width: 740, height: 220, caption: "Click Run pipeline" },
    {
        image: DeleteBranchesPreview,
        width: 750,
        height: 727,
        caption: "Pick custom: delete-store-branches, fill in the store's branch, then Run",
    },
];

const NETLIFY_PREVIEWS: LinkPreview[] = [
    { image: NetlifyDeletePreview, width: 1918, height: 791, caption: "Click Delete this project" },
];

const deleteBranchesLink = (repository: string) => ({
    label: "Open Bitbucket",
    href: `${BITBUCKET_WORKSPACE_URL}/${repository}/pipelines`,
    previews: DELETE_BRANCHES_PREVIEWS,
});

const GONE = "Already gone";

const CHECK_FAILED = "We couldn't check this right now. Try Re-check all.";

const isOffline = (store: Store) => store.liveState === null || store.liveState === 0;

const liveStateOf = (store: Store) =>
    store.liveState === 2 ? "Live" : store.liveState === 1 ? "In Progress" : "Offline";

const stateOf = (status: StoreComponentStatus): StepState => {
    if (status === "checking") return "checking";
    return status === "deleted" ? "done" : "ready";
};

const warningOf = (status: StoreComponentStatus) =>
    status === "unhealthy" ? CHECK_FAILED : undefined;

const branchesDetail = (repository: string, { branchCount }: DeploymentBranchesResult) => {
    if (branchCount === 0) return `Bitbucket · ${repository}`;

    return `Bitbucket · ${repository} · ${branchCount === 1 ? "1 branch" : `${branchCount} branches`}`;
};

const netlifyDetail = ({ status, name }: NetlifySiteResult) =>
    name ? `Netlify · ${name}` : status === "deleted" ? "Netlify · no site deployed" : "Netlify";

// Netlify's delete button lives on the project configuration page
const netlifyDeleteUrl = (adminUrl: string) => `${adminUrl.replace(/\/+$/, "")}/configuration/project#danger-zone`;

const lock = (step: Step, reason: string): Step =>
    step.state === "ready" ? { ...step, state: "locked", lockedReason: reason } : step;

// Steps 1–2 use other tabs' permissions; everything else comes with the Delete Store scopes
export type StepAccess = { syncSettings: boolean; retailSystem: boolean };

export function buildDecommissionSteps(store: Store, statuses: ComponentStatuses, access: StepAccess): Step[] {
    const { searchIndex, mirror, shopify, basicTheme, hydrogenV2, netlify } = statuses;

    const syncOff: Step = {
        key: "syncOff",
        kind: "automatic",
        title: "Turn off syncing",
        detail: `Sync Settings · ${store.enabled ? "Enabled" : "Disabled"}`,
        description: "Stops all syncs so nothing gets re-created while you clean up. Every other step waits for this one.",
        state: store.enabled ? "ready" : "done",
        doneNote: "Sync is off",
        action: { label: "Turn off syncing", tone: "primary" },
    };

    const cleanupSteps: Step[] = [
        {
            key: "offline",
            kind: "automatic",
            title: "Take the store offline",
            detail: `RetailSystem · ${liveStateOf(store)}`,
            description: "Stops the website from getting data from RetailSystem. You can set it back to Live from the RetailSystem tab.",
            state: isOffline(store) ? "done" : "ready",
            doneNote: "Offline",
            action: { label: "Take offline", tone: "primary" },
        },
        {
            key: "searchIndex",
            kind: "automatic",
            title: "Delete product search database",
            detail: "OpenSearch · product index",
            description: "Removes the list of products the website uses for its search.",
            state: stateOf(searchIndex),
            doneNote: GONE,
            warning: warningOf(searchIndex),
            action: { label: "Delete", tone: "danger" },
        },
        {
            key: "mirror",
            kind: "automatic",
            title: "Delete store database",
            detail: "SQL Server · mirror schema",
            description: "Removes the copy of the store's catalog that the sync keeps in our database.",
            state: stateOf(mirror),
            doneNote: GONE,
            warning: warningOf(mirror),
            action: { label: "Delete", tone: "danger" },
        },
        {
            key: "shopify",
            kind: "manual",
            title: "Uninstall the app from Shopify",
            detail: store.storeUrl,
            description: "Remove our app from the store's Shopify admin. This step is marked done once Shopify stops letting us in.",
            // Shopify keeps answering until the app is uninstalled, then rejects our token
            state: shopify === "checking" ? "checking" : shopify === "healthy" ? "ready" : "done",
            doneNote: "Disconnected",
            link: { label: "Open Shopify", href: shopifyAppsUrl(store.storeUrl) },
        },
        {
            key: "basicTheme",
            kind: "manual",
            title: "Delete codebase version 1 workspace",
            detail: branchesDetail("hydrogen-basic-theme", basicTheme),
            description: "Run the delete-store-branches pipeline in Bitbucket to remove this store's branches from codebase version 1.",
            state: stateOf(basicTheme.status),
            doneNote: GONE,
            warning: warningOf(basicTheme.status),
            link: deleteBranchesLink("hydrogen-basic-theme"),
        },
        {
            key: "hydrogenV2",
            kind: "manual",
            title: "Delete codebase version 2 workspace",
            detail: branchesDetail("ws-v2-hydrogen-2.0", hydrogenV2),
            description: "Run the delete-store-branches pipeline in Bitbucket to remove this store's branches from codebase version 2.",
            state: stateOf(hydrogenV2.status),
            doneNote: GONE,
            warning: warningOf(hydrogenV2.status),
            link: deleteBranchesLink("ws-v2-hydrogen-2.0"),
        },
        {
            key: "netlify",
            kind: "manual",
            title: "Remove website hosting",
            detail: netlifyDetail(netlify),
            description: "Delete the site from Netlify, where the website is hosted.",
            state: stateOf(netlify.status),
            doneNote: GONE,
            warning: warningOf(netlify.status),
            link: netlify.adminUrl
                ? {
                    label: "Open Netlify",
                    href: netlifyDeleteUrl(netlify.adminUrl),
                    previews: NETLIFY_PREVIEWS,
                }
                : undefined,
        },
    ];

    const syncOffStep = access.syncSettings ? syncOff : lock(syncOff, "Needs Sync Settings access");

    const cleanup = cleanupSteps.map((step) => {
        if (store.enabled) return lock(step, "Turn off syncing first");

        return step.key === "offline" && !access.retailSystem ? lock(step, "Needs RetailSystem access") : step;
    });

    const cancel: Step = {
        key: "cancel",
        kind: "final",
        title: "Cancel the store",
        detail: "RS Admin Portal record",
        description: "Permanently removes the store from the RS Admin Portal. This can't be undone.",
        state: "ready",
        action: { label: "Cancel store", tone: "danger" },
    };

    const isEverythingDone = [syncOff, ...cleanup].every((step) => step.state === "done");

    return [syncOffStep, ...cleanup, isEverythingDone ? cancel : lock(cancel, "Finish all steps first")];
}
